// src/features/admin/components/settings/system-preferences/components/TypographySettings.tsx
import React from 'react';
import { DocumentTextIcon, AdjustmentsHorizontalIcon, EyeIcon, SwatchIcon } from '@heroicons/react/24/outline';
import { usePreferences } from '../PreferencesContext';
import { ACCENT_COLOR_OPTIONS, NEUTRAL_BASE_OPTIONS } from '@/types/appearance';

const SYSTEM_FONTS = [
  { value: 'inter', label: 'Inter (Recommended)', category: 'Modern Sans-Serif' },
  { value: 'roboto', label: 'Roboto', category: 'Sans-Serif' },
  { value: 'open-sans', label: 'Open Sans', category: 'Sans-Serif' },
  { value: 'system-ui', label: 'System UI', category: 'Native' },
];

const DOCUMENT_FONTS = [
  // Court-Approved Serif Fonts
  { value: 'times-new-roman', label: 'Times New Roman', category: 'Court-Approved Serif' },
  { value: 'edgedocs-roman', label: 'EdgeDocs Roman', category: 'Court-Approved Serif' },
  { value: 'liberation-serif', label: 'Liberation Serif', category: 'Court-Approved Serif' },
  { value: 'dejavu-serif', label: 'DejaVu Serif', category: 'Court-Approved Serif' },
  { value: 'nimbus-roman', label: 'Nimbus Roman', category: 'Court-Approved Serif' },
  { value: 'tex-gyre-termes', label: 'TeX Gyre Termes', category: 'Court-Approved Serif' },
  { value: 'source-serif-4', label: 'Source Serif 4', category: 'Court-Approved Serif' },
  
  // Modern Sans-Serif Fonts
  { value: 'inter', label: 'Inter', category: 'Modern Sans-Serif' },
  { value: 'roboto', label: 'Roboto', category: 'Modern Sans-Serif' },
  { value: 'liberation-sans', label: 'Liberation Sans', category: 'Modern Sans-Serif' },
  { value: 'dejavu-sans', label: 'DejaVu Sans', category: 'Modern Sans-Serif' },
  { value: 'noto-sans', label: 'Noto Sans', category: 'Modern Sans-Serif' },
  
  // Monospace Fonts
  { value: 'dejavu-sans-mono', label: 'DejaVu Sans Mono', category: 'Monospace' },
  { value: 'jetbrains-mono', label: 'JetBrains Mono', category: 'Monospace' },
];

const FONT_SIZES = [
  { value: 'small', label: 'Small', description: 'Compact display (90%)' },
  { value: 'medium', label: 'Medium', description: 'Standard size (100%)' },
  { value: 'large', label: 'Large', description: 'Enhanced readability (110%)' },
  { value: 'x-large', label: 'Extra Large', description: 'Maximum accessibility (125%)' },
];

const CONTRAST_MODES = [
  { value: 'standard', label: 'Standard Contrast', description: 'Default appearance' },
  { value: 'high', label: 'High Contrast', description: 'WCAG AAA compliant' },
];

const MEASUREMENT_UNITS = [
  { value: 'inches', label: 'Inches', description: 'US Standard' },
  { value: 'centimeters', label: 'Centimeters', description: 'Metric' },
  { value: 'points', label: 'Points', description: 'Typography (72pt = 1 inch)' },
];

const UI_THEME_MODES = [
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
  { value: 'system', label: 'System' },
];

const UI_DENSITY = [
  { value: 'compact', label: 'Compact' },
  { value: 'cozy', label: 'Cozy' },
  { value: 'comfortable', label: 'Comfortable' },
];

const TypographySettings: React.FC = () => {
  const { preferences, updatePreference } = usePreferences();

  if (!preferences) {
    return <div>Loading...</div>;
  }

  const systemFont = preferences.system_font;
  const documentFont = preferences.document_font;
  const fontSize = preferences.font_size_scale;
  const contrastMode = preferences.contrast_mode;
  const measurementUnit = preferences.measurement_unit;
  const uiThemeMode = preferences.ui_theme_mode || 'system';
  const uiAccentColor = preferences.ui_accent_color || 'blue';
  const uiAccentCustom = preferences.ui_accent_custom_hex || '#3b82f6';
  const uiDensity = preferences.ui_density || 'cozy';
  const uiNeutralBase = preferences.ui_neutral_base || 'zinc';

  return (
    <div className="typography-settings space-y-8">
      {/* UI Appearance Defaults */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <SwatchIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Interface Appearance Defaults
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set firm-wide default theme settings. Users can override these in their personal preferences.
            </p>
          </div>
        </div>

        <div className="ml-9 space-y-4">
          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Theme mode</div>
            <div className="flex flex-wrap gap-2">
              {UI_THEME_MODES.map((mode) => (
                <button
                  key={mode.value}
                  onClick={() => updatePreference('ui_theme_mode', mode.value as any)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                    uiThemeMode === mode.value
                      ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {mode.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Accent color</div>
            <div className="flex flex-wrap gap-2">
              {ACCENT_COLOR_OPTIONS.map((color) => (
                <button
                  key={color.id}
                  onClick={() => updatePreference('ui_accent_color', color.id as any)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs transition-colors ${
                    uiAccentColor === color.id
                      ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft'
                      : 'border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span
                    className="h-4 w-4 rounded-full border border-white/40"
                    style={{ backgroundColor: color.id === 'custom' ? uiAccentCustom : color.colorValue }}
                  />
                  {color.label}
                </button>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-3">
              <span className="text-xs text-gray-500 dark:text-gray-400">Custom</span>
              <input
                type="color"
                value={uiAccentCustom}
                onChange={(event) => {
                  updatePreference('ui_accent_color', 'custom' as any);
                  updatePreference('ui_accent_custom_hex', event.target.value as any);
                }}
                className="h-8 w-12 rounded-md border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-700"
              />
              <span className="text-xs text-gray-500 dark:text-gray-400">{uiAccentCustom.toUpperCase()}</span>
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Density</div>
            <div className="flex flex-wrap gap-2">
              {UI_DENSITY.map((option) => (
                <button
                  key={option.value}
                  onClick={() => updatePreference('ui_density', option.value as any)}
                  className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                    uiDensity === option.value
                      ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Neutral base</div>
            <div className="flex flex-wrap gap-2">
              {NEUTRAL_BASE_OPTIONS.map((base) => (
                <button
                  key={base.id}
                  onClick={() => updatePreference('ui_neutral_base', base.id as any)}
                  title={base.description}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${
                    uiNeutralBase === base.id
                      ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft text-lex-accent dark:text-lex-accent'
                      : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <span
                    className="h-4 w-4 rounded-full border border-white/40"
                    style={{ backgroundColor: base.swatch }}
                  />
                  {base.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Controls the gray palette used for backgrounds, borders, and text throughout the app.
            </p>
          </div>
        </div>
      </div>
      {/* System-Wide UI Font */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <DocumentTextIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              System-Wide Default Font
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose the primary font for the application interface (menus, buttons, dialogs).
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={systemFont}
            onChange={(e) => updatePreference('system_font', e.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {SYSTEM_FONTS.map(font => (
              <option key={font.value} value={font.value}>
                {font.label} - {font.category}
              </option>
            ))}
          </select>

          {/* Preview */}
          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-600">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Preview:</p>
            <p className="text-base" style={{ fontFamily: systemFont }}>
              The quick brown fox jumps over the lazy dog.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              ABCDEFGHIJKLMNOPQRSTUVWXYZ 0123456789
            </p>
          </div>
        </div>
      </div>

      {/* Document Default Font */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <DocumentTextIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Document Default Font
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Set the default font for new legal documents. This can be overridden per template or document type.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={documentFont}
            onChange={(e) => updatePreference('document_font', e.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {DOCUMENT_FONTS.map(font => (
              <option key={font.value} value={font.value}>
                {font.label} - {font.category}
              </option>
            ))}
          </select>

          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            💡 Tip: Manage additional fonts in <span className="font-medium">Document Management → Font Manager</span>
          </p>
        </div>
      </div>

      {/* Font Size Scale */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <AdjustmentsHorizontalIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Font Size Scale
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Adjust the base font size for the entire application. This affects readability and accessibility.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <div className="space-y-3">
            {FONT_SIZES.map(size => (
              <label
                key={size.value}
                className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  fontSize === size.value
                    ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="fontSize"
                  value={size.value}
                  checked={fontSize === size.value}
                  onChange={(e) => updatePreference('font_size_scale', e.target.value)}
                  className="h-4 w-4 text-lex-accent focus:ring-lex-accent"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{size.label}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{size.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Contrast Mode */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <EyeIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Contrast Mode
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Enhance visibility for users with visual impairments or in bright environments.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <div className="space-y-3">
            {CONTRAST_MODES.map(mode => (
              <label
                key={mode.value}
                className={`flex items-center space-x-3 p-3 rounded-lg border-2 cursor-pointer transition-colors ${
                  contrastMode === mode.value
                    ? 'border-lex-accent bg-lex-accent-soft dark:bg-lex-accent-soft'
                    : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                }`}
              >
                <input
                  type="radio"
                  name="contrastMode"
                  value={mode.value}
                  checked={contrastMode === mode.value}
                  onChange={(e) => updatePreference('contrast_mode', e.target.value)}
                  className="h-4 w-4 text-lex-accent focus:ring-lex-accent"
                />
                <div className="flex-1">
                  <div className="font-medium text-gray-900 dark:text-gray-100">{mode.label}</div>
                  <div className="text-sm text-gray-500 dark:text-gray-400">{mode.description}</div>
                </div>
              </label>
            ))}
          </div>
        </div>
      </div>

      {/* Measurement Units */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
        <div className="flex items-start mb-4">
          <AdjustmentsHorizontalIcon className="h-6 w-6 text-lex-accent mr-3 mt-1" />
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-1">
              Measurement Units
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Choose the preferred unit system for page layout, margins, and spacing in documents.
            </p>
          </div>
        </div>

        <div className="ml-9">
          <select
            value={measurementUnit}
            onChange={(e) => updatePreference('measurement_unit', e.target.value)}
            className="block w-full max-w-md px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:ring-lex-accent focus:border-lex-accent bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
          >
            {MEASUREMENT_UNITS.map(unit => (
              <option key={unit.value} value={unit.value}>
                {unit.label} - {unit.description}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
};

export default TypographySettings;
